import { Box, Grid, TableContainer } from '@mui/material';
import { mainTableColumnTitles } from './consts';
import Button from '../../../../components/button/Button';
import DataTable from '../../../../components/DataTable/DataTable';
import { NoDataWithCheck } from '../../components/NoDataWithCheck';
import BackdropComponent from '../../../../components/Backdrop/BackdropComponent';
import DrawerManager from './components/drawers';
import useRadiologicalEquipmentModule from './hooks/useRadiologicalEquipmentModule';
import TableActionButton from '../../../Presentaciones/components/TableActionButton';
import EyeIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import InformationStateBadge from '../../components/InformationStateBadge';
import { CarcinogenicModuleFooter } from '../../components/CarcinogenicModuleFooter';
import { HandleTabChange, TAB_INDEX } from '../../pages';
import { validateEquipmentState } from './helpers';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../../hooks/reduxHooks';

interface RadiologicalEquipmentTabContentProps {
    handleTabChange: HandleTabChange;
    showCancelCarcinogenicReportAlert: any;
    onlyVisual: boolean;
}

const RadiologicalEquipmentTabContent = ({
    handleTabChange,
    showCancelCarcinogenicReportAlert,
    onlyVisual
}: RadiologicalEquipmentTabContentProps) => {
    /*
     * La tabla mostraba `idTipoEquipo` crudo ("3"). El nombre está en los datos
     * paramétricos que ya carga el drawer de alta; acá sólo se resuelve.
     */
    const tiposEquipo = useAppSelector(
        (state) => state.carcinogenicReport.carcinogenicParametricData?.tiposEquipo
    );
    const nombreTipoEquipo = (idTipoEquipo: number | null | undefined) => {
        if (idTipoEquipo == null) return '-';
        const tipo = (tiposEquipo || []).find((t: any) => t?.id === idTipoEquipo);
        return tipo?.descripcion || `${idTipoEquipo}`;
    };
    const {
        selected,
        setSelected,
        open,
        setOpen,
        translate,
        carcinogenicReport,
        isDeletingEquipment,
        radiologicalEmptyDeclarationCheckState,
        isEditingCarcinogenicReportRadiologicalCheck,
        handleEditIconClick,
        handleClickNewEquipmentButton,
        handleDeleteEquipmentIconClick,
        handleEditEmptyRadiologicalCheck,
    } = useRadiologicalEquipmentModule({});

    const navigate = useNavigate();

    const handleFooterCancelClick = () => {
        if (onlyVisual) {
            handleTabChange(TAB_INDEX.PROVIDERS_AND_BUYERS);
            return;
        }
        showCancelCarcinogenicReportAlert({ confirmAction: () => navigate(-1) });
    };

    const sustancias = carcinogenicReport?.sustancias || [];

    const transformedRadilogicalEquipments = sustancias.reduce((acc: any, sustancia: any) => {
        return [
            ...acc,
            ...sustancia?.equipos?.map((equipo: any) => ({
                ...equipo,
                sustancia: { id: sustancia?.id, descripcion: sustancia?.descripcion }
            }))
        ];
    }, []);

    const isLoading = false;

    return (
        <>
            <Box bgcolor={'#ffffff'} borderRadius={'0px 0px 6px 6px'} p={3}>
                <Grid container justifyContent={'flex-end'}>
                    {!onlyVisual && (
                        <Button
                            type="contained"
                            onClick={() => {
                                handleClickNewEquipmentButton();
                            }}
                            loading={false}
                        >
                            {translate('newRadiologicalEquipment')}
                        </Button>
                    )}
                </Grid>
                <Grid item xs={12} mt={3}>
                    {transformedRadilogicalEquipments?.length === 0 ? (
                        <NoDataWithCheck
                            title={translate('noData.title')}
                            subtitle={translate('noData.subtitle1')}
                            extraSubtitle={translate('noData.subtitle2')}
                            declareNoDataText={translate('noData.declareNoData')}
                            checkState={radiologicalEmptyDeclarationCheckState}
                            onClickCheck={() => {
                                if (onlyVisual) return;
                                handleEditEmptyRadiologicalCheck();
                            }}
                        />
                    ) : (
                        <TableContainer>
                            <DataTable
                                testID="radiological-equipment-table"
                                columnTitles={mainTableColumnTitles(translate)}
                                columnWidths={[]}
                                onSelectRow={() => {}}
                                selected={null}
                                selectedItemFromAction={null}
                                menuOpen={false}
                                items={{
                                    data: { data: transformedRadilogicalEquipments || [] }
                                }}
                                loading={false}
                                filters={{}}
                                setFilters={() => {}}
                                isFetching={false}
                                headers={[
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) => `${item?.marca || '-'}`
                                    },
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) => `${item?.modelo || '-'}`
                                    },
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) => nombreTipoEquipo(item?.idTipoEquipo)
                                    },
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) =>
                                            `${item?.sustancia?.descripcion || '-'}`
                                    },
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) => {
                                            const result = validateEquipmentState(item);

                                            return (
                                                <InformationStateBadge
                                                    state={
                                                        result?.isValid ? 'complete' : 'incomplete'
                                                    }
                                                />
                                            );
                                        }
                                    },
                                    {
                                        type: 'data',
                                        lowerLabel: (item: any) => (
                                            <Box display={'flex'} gap={1.5}>
                                                {!onlyVisual && (
                                                    <TableActionButton
                                                        disabled={false}
                                                        onClick={() => {
                                                            handleDeleteEquipmentIconClick(item);
                                                        }}
                                                        IconComponent={DeleteIcon}
                                                    />
                                                )}
                                                <TableActionButton
                                                    disabled={false}
                                                    onClick={() => handleEditIconClick(item)}
                                                    IconComponent={!onlyVisual ? EditIcon : EyeIcon}
                                                />
                                            </Box>
                                        )
                                    }
                                ]}
                            />
                        </TableContainer>
                    )}
                </Grid>
                <DrawerManager
                    open={open}
                    setOpen={setOpen}
                    selectedRadiologicalEquipment={selected}
                    translate={translate}
                    transformedRadilogicalEquipments={transformedRadilogicalEquipments}
                    onlyVisual={onlyVisual}
                />
                <BackdropComponent
                    loading={
                        isLoading ||
                        isDeletingEquipment ||
                        isEditingCarcinogenicReportRadiologicalCheck
                    }
                />
            </Box>
            <CarcinogenicModuleFooter
                handleCancelClick={handleFooterCancelClick}
                handleSubmitClick={(e: any) =>
                    handleTabChange(e, TAB_INDEX.WORKERS_AND_MEDICAL_EXAMS)
                }
                onlyVisual={onlyVisual}
            />
        </>
    );
};

export default RadiologicalEquipmentTabContent;
